import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resumeSessionByCard } from "../services/apiService";
import { useErrorHandler } from "../utils/errorHandler";
import analyticsService from "../services/analyticsService";
import { setTokens } from "../services/tokenService";
import { buildSupportWhatsAppUrl } from "../constants/support";
import { buildStationPanel } from "../utils/stationPanel";
import ScreenLayout from "./ui/ScreenLayout";
import Button from "./ui/Button";
import Icon from "./ui/Icon";

const MAX_ATTEMPTS = 3;
const DIGIT_COUNT = 4;
const EMPTY_DIGITS = Array(DIGIT_COUNT).fill("");

// Écrans 2b / 2c : le client retrouve sa recharge avec les 4 derniers chiffres
// de sa carte. En cas d'erreur, le message reste neutre : il ne dit pas si une
// recharge existe pour ces chiffres.
export default function ResumeSessionComponent() {
  const { t, i18n } = useTranslation();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();
  const location = useLocation();
  const { evcpData, connector, reference } = location.state || {};

  const [digits, setDigits] = useState(EMPTY_DIGITS);
  const [mismatch, setMismatch] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);
  const [locked, setLocked] = useState(false);
  const inputsRef = useRef([]);

  const hasRequiredData = Boolean(evcpData?.reference && connector?.id);
  const code = digits.join("");

  // Vérification des informations nécessaires
  useEffect(() => {
    if (!hasRequiredData) {
      navigate("/error", {
        state: { errorText: t("resumeSession.errors.missingInfo") },
      });
      return;
    }
    analyticsService.pushPageView("resume_session_verification", {
      station_id: evcpData.reference,
    });
  }, [hasRequiredData, evcpData, navigate, t]);

  const focusDigit = (index) => inputsRef.current[Math.max(0, Math.min(index, DIGIT_COUNT - 1))]?.focus();

  // Un chiffre par case ; un collage de plusieurs chiffres remplit les cases suivantes.
  const handleDigitChange = (index, rawValue) => {
    const typed = rawValue.replace(/\D/g, "");
    const next = [...digits];

    if (!typed) {
      next[index] = "";
    } else {
      typed
        .slice(0, DIGIT_COUNT - index)
        .split("")
        .forEach((digit, offset) => {
          next[index + offset] = digit;
        });
      focusDigit(index + typed.length);
    }

    setDigits(next);
    setMismatch(false);
    setError("");
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusDigit(index - 1);
    }
    if (event.key === "Enter") handleSubmit();
  };

  const handleSubmit = async () => {
    if (locked || isSubmitting) return;
    if (code.length !== DIGIT_COUNT) {
      setError(t("resumeSession.errors.invalidFormat"));
      return;
    }

    setIsSubmitting(true);
    setError("");
    analyticsService.pushEvent("resume_session_submitted", { station_id: evcpData.reference });

    try {
      const data = await resumeSessionByCard(connector.id, code);

      setTokens(data);

      analyticsService.pushEvent("resume_session_success", {
        station_id: evcpData.reference,
      });

      navigate("/start-charging", {
        state: {
          accessToken: data.accessToken,
          connector,
          evcpData,
          finishedTransaction: data.finishedTransaction || null,
        },
      });
    } catch (err) {
      const httpStatus = err?.status;

      if (httpStatus === 401) {
        // Chiffres incorrects : décrémente le compteur de tentatives
        const remaining = attemptsLeft - 1;
        setAttemptsLeft(remaining);
        setMismatch(true);
        analyticsService.pushEvent("resume_session_failed", {
          station_id: evcpData.reference,
          reason: "card_mismatch",
          attempts_left: remaining,
        });

        if (remaining <= 0) {
          setLocked(true);
          setError(t("resumeSession.errors.tooManyAttempts"));
        }
      } else if (httpStatus === 404) {
        // La session s'est terminée entre le scan et la validation → parcours standard
        analyticsService.pushEvent("resume_session_failed", {
          station_id: evcpData.reference,
          reason: "no_session",
        });
        navigate(`/${reference}`, { replace: true });
      } else {
        setError(handleError(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasRequiredData) {
    return null;
  }

  const boxClasses = locked
    ? "bg-qv-canvas border-b border-qv-line-strong !text-[#8B8B93]"
    : mismatch
      ? "bg-qv-red-50 border-b-2 border-qv-red"
      : "bg-[rgba(91,62,255,.06)] border-b border-qv-brand focus:border-b-2";

  const whatsappButton = (className = "") => (
    <Button
      variant="whatsapp"
      icon="whatsapp"
      className={className}
      href={buildSupportWhatsAppUrl(t, { station: evcpData.reference, connector: connector.reference })}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        analyticsService.pushEvent("support_whatsapp_clicked", {
          station_id: evcpData.reference,
          context: "resume_session",
        })
      }
    >
      {t("support.whatsapp")}
    </Button>
  );

  // « Trop de tentatives. Veuillez réessayer plus tard. » : la première phrase en gras.
  const lockedMessage = t("resumeSession.errors.tooManyAttempts").match(/^(.+?[.!])\s+(.+)$/);
  const showHelp = mismatch || locked;

  return (
    <ScreenLayout
      onBack={() => navigate(-1)}
      panel={buildStationPanel({ t, language: i18n.language, evcpData, connector, withStatus: true })}
      footer={
        <>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={code.length !== DIGIT_COUNT || locked}
          >
            {isSubmitting ? t("resumeSession.verifying") : t("resumeSession.access")}
          </Button>
          {/* Version web : l'aide vient sous le bouton, séparée par un filet. */}
          {showHelp && (
            <div className="hidden md:flex mt-3.5 items-center gap-4 border-t border-[#F0F0F2] pt-6">
              <p className="flex-1 text-sm font-medium text-qv-muted">{t("refused.needHelp")}</p>
              {whatsappButton("!w-auto !px-5")}
            </div>
          )}
        </>
      }
    >
      <h1 className="mb-2 text-[28px] leading-9 tracking-[-.02em] font-bold md:text-[32px] md:leading-10">
        {t("resumeSession.findTitle")}
      </h1>
      <p className="mb-6 text-[15px] leading-6 text-qv-muted md:mb-7">{t("resumeSession.findSubtitle")}</p>

      <div className="grid grid-cols-4 gap-3 md:grid-cols-[repeat(4,72px)] md:gap-4">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputsRef.current[index] = element;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={index === 0 ? DIGIT_COUNT : 1}
            value={digit}
            disabled={locked || isSubmitting}
            onChange={(event) => handleDigitChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onFocus={(event) => event.target.select()}
            aria-label={t("resumeSession.digitLabel", { position: index + 1 })}
            aria-invalid={mismatch}
            className={`h-16 w-full rounded-none text-center text-[28px] font-bold font-montserrat text-qv-ink outline-none md:h-[72px] md:text-[30px] ${
              locked ? "" : "disabled:opacity-60"
            } ${boxClasses}`}
          />
        ))}
      </div>

      {mismatch && !locked && (
        <p className="mt-3 flex gap-2 items-start text-sm leading-5 font-medium text-qv-red" role="alert">
          <Icon name="danger-circle-linear" size={18} className="mt-px" />
          {t("resumeSession.errors.wrongDigits")}
        </p>
      )}
      {locked && (
        <div className="mt-4 flex gap-2.5 items-start rounded-xl bg-qv-red-50 px-4 py-3.5 text-sm leading-5 font-medium text-qv-red" role="alert">
          <Icon name="lock-keyhole-minimalistic-linear" size={20} />
          <p>
            {lockedMessage ? (
              <>
                <b>{lockedMessage[1]}</b> {lockedMessage[2]}
              </>
            ) : (
              t("resumeSession.errors.tooManyAttempts")
            )}
          </p>
        </div>
      )}
      {error && !locked && (
        <p className="mt-3 flex gap-2 items-start text-sm leading-5 font-medium text-qv-red" role="alert">
          <Icon name="danger-circle-linear" size={18} className="mt-px" />
          {error}
        </p>
      )}

      <p className="mt-6 flex gap-2.5 items-start text-[13px] leading-[19px] font-medium text-qv-muted md:text-sm md:leading-5">
        <Icon name="letter-linear" size={18} className="text-qv-brand" />
        {t("resumeSession.emailLinkHint")}
      </p>

      {/* Aide proposée dès la première erreur (2c). */}
      {showHelp && (
        <div className="mt-5 flex flex-col gap-2 md:hidden">
          <p className="text-center text-[13px] font-medium text-qv-muted">{t("refused.needHelp")}</p>
          {whatsappButton()}
        </div>
      )}
    </ScreenLayout>
  );
}
